import * as routerActions from 'connected-react-router'

// Actions
import { actions as notificationsActions } from 'modules/notifications/actions'
import { actions as audioActions } from 'modules/audio/actions'
import { actions as browseActions, parseQueryForSearch } from 'modules/browse/actions'

// Selectors
import { selectTrackListFromTrackIds, selectTrackListWithoutRegionLockedTracks } from 'modules/browse/selectors'

// Types
import { Dispatch, GetState, APIAction, Loop, EntityState, Collection } from 'types'
import { collectionSchema, collectionListSchema } from 'modules/collections/models'

// Utils
import jsTracking from 'lib/jsTracking'

export const actions = {
  _createCollection: (data: Record<string, unknown>): APIAction => ({
    CALL_API: {
      type: 'COLLECTION_CREATE',
      method: 'POST',
      endpoint: '/api/collection/',
      schema: collectionSchema,
      data,
    },
  }),

  createCollection: (data: { tracks: Array<{ id: number; loop?: Loop }> }) => async (dispatch: Dispatch) => {
    if (data.tracks) {
      jsTracking('AddToWishlist')
    }
    const resp = (await dispatch(actions._createCollection(data))) as { data: { response: { result: number } } }
    if (data.tracks && data.tracks.length) {
      return dispatch(
        notificationsActions.createTrackAddedToCollectionNotification(data.tracks[0].id, resp.data.response.result, !!data.tracks[0].loop),
      )
    }
    return resp
  },

  updateCollection: (collectionId: number, data: Record<string, unknown>): APIAction => ({
    CALL_API: {
      type: 'COLLECTION_UPDATE',
      method: 'PUT',
      endpoint: `/api/collection/${collectionId}/`,
      schema: collectionSchema,
      data: { ...data, public: !!data.public },
    },
  }),

  deleteCollection: (collectionId: number, redirect?: string) => (dispatch: Dispatch) => {
    dispatch({
      CALL_API: {
        type: 'COLLECTION_DELETE',
        method: 'DELETE',
        endpoint: `/api/collection/${collectionId}/`,
        schema: collectionSchema,
        actionParams: {
          collectionId,
        },
      },
    })
    if (redirect) {
      dispatch(routerActions.push(redirect))
    }
  },

  fetchCollection: (id: number): APIAction => ({
    CALL_API: {
      type: 'FETCH_COLLECTION',
      method: 'GET',
      endpoint: `/api/collection/${id}/`,
      schema: collectionSchema,
      requestKey: String(id),
      actionParams: { identifier: 'test' },
    },
  }),

  fetchCollections: (queryString: string, page = 1, size = 25) => {
    const parsedQueryString = parseQueryForSearch(queryString, page)
    return {
      CALL_API: {
        type: 'FETCH_COLLECTIONS',
        method: 'GET',
        endpoint: `/api/collection/?${parsedQueryString}&size=${size}`,
        requestKey: queryString,
        schema: collectionListSchema,
        actionParams: { page, queryString },
      },
    }
  },

  fetchOwnCollections: () => (dispatch: Dispatch, getState: GetState) => {
    const state = getState()
    const { profileId } = state.auth
    const profile = state.entities.profiles[String(profileId)]
    const query = `?owner=${profile.user}`
    if (profile) {
      dispatch(actions.fetchCollections(query, 1, 100))
    }
  },

  removeTrackFromCollection: (trackId: number, collectionId: number, loop: Loop | null | undefined, requestKey?: string): APIAction => ({
    CALL_API: {
      type: 'COLLECTION_REMOVE',
      method: 'POST',
      endpoint: `/api/collection/${collectionId}/remove/`,
      schema: collectionSchema,
      replaceItems: true,
      requestKey: requestKey || String(collectionId),
      data: {
        collection_id: collectionId,
        track_id: trackId,
        ...(loop ? { beat_start: loop.beat_start, beat_count: loop.beat_count } : {}),
      },
    },
  }),

  addTrackToCollection:
    (trackId: number, collectionId: number, loop: Loop | null | undefined, requestKey?: string) => async (dispatch: Dispatch) => {
      dispatch(notificationsActions.createTrackAddedToCollectionNotification(trackId, collectionId, !!loop))
      const response = (await dispatch({
        CALL_API: {
          type: 'COLLECTION_ADD',
          method: 'POST',
          endpoint: `/api/collection/${collectionId}/add/`,
          schema: collectionSchema,
          requestKey: requestKey || String(collectionId),
          data: {
            track_id: trackId,
            ...(loop
              ? {
                  beat_start: loop.beat_start,
                  beat_count: loop.beat_count,
                }
              : {}),
          },
        },
      })) as { data: { error?: string } }
      if (response.data.error) {
        return dispatch(notificationsActions.createError("Couldn't save track!"))
      }
      jsTracking('AddToWishlist')
      return Promise.resolve()
    },

  playCollection: (id: number) => async (dispatch: Dispatch, getState: GetState) => {
    dispatch(audioActions.reset())
    dispatch({ type: 'PREPARE_PLAY_COLLECTION_START' })
    const contextKey = `{"collection":"${id}"}`
    dispatch(audioActions.setPlaylist([], contextKey, true)) // set empty playlist while loading for correct active loading state

    const resp = (await dispatch(actions.fetchCollection(id))) as {
      data: { response: { entities: { collections: EntityState<Collection> } } }
    }

    const trackIds: Array<{ id: number }> = resp.data.response.entities.collections[id].tracks?.slice(0, 20)
    const trackIdsToPlay: Array<number> = trackIds?.map(({ id: trackId }) => trackId)
    await dispatch(browseActions.fetchTracksBySearchQuery(`ids=${trackIdsToPlay.join('__')}`))
    const state = getState()
    const pureTrackList = selectTrackListFromTrackIds(state, trackIds)
    const trackList = selectTrackListWithoutRegionLockedTracks(state, pureTrackList)
    dispatch(audioActions.setPlaylist(trackList, contextKey, true)) // update with real tracklist
    dispatch({ type: 'PREPARE_PLAY_COLLECTION_DONE' })
    return dispatch(audioActions.playPlaylist(contextKey))
  },
}
